import { SchoolRestrictionModel } from "@/types/school.model";
import { RESTRICTION } from "@/utils/constants";
import { ToggleSwitchOffSVG, ToggleSwitchOnSVG } from "@/utils/svg";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Modal from "./Modal";
import SelectWithRadio from "./SelectWithRadio";
import { Button } from "./button";
import Checkbox from "./checkbox";
import { Input } from "./input";
import TimePickerDefault from "./time-picker-default";
import { AppDispatch } from "@/store";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSchoolActionLoading,
  updateRestriction,
} from "@/store/slices/schoolSlice";
import moment from "moment";

// Define the form data type to match SchoolRestrictionModel
export type RestrictionFormData = {
  is_active: boolean;
  restriction_type: "HOURS" | "DAYS" | "TIME";
  hours?: string;
  days?: {
    MONDAY?: boolean;
    TUESDAY?: boolean;
    WEDNESDAY?: boolean;
    THURSDAY?: boolean;
    FRIDAY?: boolean;
    SATURDAY?: boolean;
    SUNDAY?: boolean;
  };
  time_ranges?: {
    MONDAY?: { start?: string; end?: string } | string;
    TUESDAY?: { start?: string; end?: string } | string;
    WEDNESDAY?: { start?: string; end?: string } | string;
    THURSDAY?: { start?: string; end?: string } | string;
    FRIDAY?: { start?: string; end?: string } | string;
    SATURDAY?: { start?: string; end?: string } | string;
    SUNDAY?: { start?: string; end?: string } | string;
  };
  school?: string;
};

type RestrictionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Partial<RestrictionFormData>;
  onRefresh?: () => void;
  isEditMode?: boolean;
  schoolId?: string;
};

function RestrictionModal({
  isOpen,
  onClose,
  initialData,
  onRefresh,
  isEditMode = false,
  schoolId,
}: RestrictionModalProps) {
  // console.log({ initialData });

  const dispatch = useDispatch<AppDispatch>();
  const actionLoader = useSelector(selectSchoolActionLoading);

  const [clearConfirmModal, setClearConfirmModal] = useState<{
    isOpen: boolean;
    type: "HOURS" | "DAYS" | "TIME";
  }>({
    isOpen: false,
    type: "HOURS",
  });

  const [isCleared, setIsCleared] = useState(false);

  // Initialize form with react-hook-form
  const { control, handleSubmit, reset, setValue, watch } =
    useForm<RestrictionFormData>({
      defaultValues: {
        is_active: initialData?.is_active || false,
        restriction_type: initialData?.restriction_type || "HOURS",
        hours: initialData?.hours || "1",
        days: initialData?.days || {},
        time_ranges: initialData?.time_ranges || {},
        school: initialData?.school || schoolId,
      },
      // mode: "onBlur",
      // reValidateMode: "onChange",
      // criteriaMode: "firstError",
    });

  // Watch values for conditional rendering
  const isEnabled = watch("is_active");
  const restrictionType = watch("restriction_type");
  const hoursValue = watch("hours");
  const saturdayChecked = watch("days.SATURDAY");
  const sundayChecked = watch("days.SUNDAY");

  // Get max hours based on weekend day selections
  const getMaxHours = () => {
    if (saturdayChecked && sundayChecked) {
      return 168; // Both weekend days selected
    } else if (saturdayChecked || sundayChecked) {
      return 144; // One weekend day selected
    } else {
      return 120; // No weekend days selected
    }
  };

  // Check if form is valid based on restriction type
  const isFormValid = () => {
    if (!isEnabled) return true;

    if (restrictionType === "HOURS") {
      const hours = parseInt(hoursValue || "0");
      const maxHours = getMaxHours();
      return hours > 0 && hours <= maxHours;
    }

    if (restrictionType === "DAYS") {
      // Check if at least one day is selected
      const days = watch("days");
      return days && Object.values(days).some((value) => value === true);
    }

    if (restrictionType === "TIME") {
      // Check if at least one day has both start and end times
      const timeRanges = watch("time_ranges");
      if (!timeRanges) return false;

      // First check if at least one day has both start and end times
      const hasCompleteTimeRange = Object.values(timeRanges).some(
        (range) =>
          typeof range === "object" &&
          range !== null &&
          "start" in range &&
          "end" in range &&
          !!range.start &&
          !!range.end
      );

      if (!hasCompleteTimeRange) return false;

      // Then check if any day has end time before or equal to start time
      const dayKeys = [
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
        "SATURDAY",
        "SUNDAY",
      ];
      for (const day of dayKeys) {
        const range = timeRanges[day as keyof typeof timeRanges];
        if (
          typeof range === "object" &&
          range &&
          "start" in range &&
          "end" in range &&
          range.start &&
          range.end
        ) {
          // Check if start and end times are the same
          if (areTimesEqual(range.start, range.end)) {
            return false;
          }
          // Check if end time is before start time
          if (isTimeBeforeOrEqual(range.end, range.start)) {
            return false;
          }
        }
      }

      return true;
    }

    return true;
  };

  // Get error message for hours
  const getHoursErrorMessage = () => {
    if (!hoursValue || parseInt(hoursValue) <= 0) {
      return "Hours must be greater than 0";
    }

    const hours = parseInt(hoursValue);
    const maxHours = getMaxHours();

    if (hours > maxHours) {
      if (saturdayChecked && sundayChecked) {
        return `Hours cannot exceed ${maxHours} when both Saturday and Sunday are selected`;
      } else if (saturdayChecked || sundayChecked) {
        return `Hours cannot exceed ${maxHours} when only one weekend day is selected`;
      } else {
        return `Hours cannot exceed ${maxHours} when no weekend days are selected`;
      }
    }

    return "";
  };

  // Get error message for days
  const getDaysErrorMessage = () => {
    if (restrictionType === "DAYS") {
      const days = watch("days");
      if (!days || !Object.values(days).some((value) => value === true)) {
        return "Please select at least one day";
      }
    }
    return "";
  };

  // Get error message for time ranges
  const getTimeRangesErrorMessage = () => {
    if (restrictionType === "TIME") {
      const timeRanges = watch("time_ranges");
      if (!timeRanges) return "Please set at least one day's time range";

      // Check if at least one day has both start and end times
      const hasCompleteTimeRange = Object.values(timeRanges).some(
        (range) =>
          typeof range === "object" &&
          range !== null &&
          "start" in range &&
          "end" in range &&
          !!range.start &&
          !!range.end
      );

      if (!hasCompleteTimeRange) {
        return "Please set both start and end time for at least one day";
      }

      // Check if any day has end time before or equal to start time
      const dayKeys = [
        "MONDAY",
        "TUESDAY",
        "WEDNESDAY",
        "THURSDAY",
        "FRIDAY",
        "SATURDAY",
        "SUNDAY",
      ];
      for (const day of dayKeys) {
        const range = timeRanges[day as keyof typeof timeRanges];
        if (
          typeof range === "object" &&
          range &&
          "start" in range &&
          "end" in range &&
          range.start &&
          range.end
        ) {
          if (areTimesEqual(range.start, range.end)) {
            return `Start and end time cannot be the same (${
              day.charAt(0) + day.slice(1).toLowerCase()
            })`;
          }
          if (isTimeBeforeOrEqual(range.end, range.start)) {
            return `End time cannot be before start time (${
              day.charAt(0) + day.slice(1).toLowerCase()
            })`;
          }
        }
      }
    }
    return "";
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsCleared(false);

      reset({
        is_active: false,
        restriction_type: "HOURS",
        hours: "1",
        days: {},
        time_ranges: {},
        school: schoolId,
      });
    }
  }, [isOpen, reset, schoolId]);

  // Initialize form with initial data when in edit mode
  useEffect(() => {
    if (isEditMode && initialData && isOpen) {
      // Format time values if restriction type is TIME
      let formattedTimeRanges = initialData.time_ranges || {};

      if (initialData.restriction_type === "TIME" && initialData.time_ranges) {
        formattedTimeRanges = {};
        // Process each day's time ranges to remove spaces between time and AM/PM
        Object.keys(initialData.time_ranges).forEach((day) => {
          const dayKey = day as keyof typeof initialData.time_ranges;
          const timeRange = initialData.time_ranges?.[dayKey];

          if (timeRange) {
            const [startTime, endTime] = (timeRange as string).split("-") || [];

            formattedTimeRanges[
              dayKey.toUpperCase() as keyof typeof formattedTimeRanges
            ] = {
              start: startTime
                ? moment(startTime, "HH:mm").format("HH:mm")
                : "",
              end: endTime ? moment(endTime, "HH:mm").format("HH:mm") : "",
            };
          }
        });
      }

      reset({
        is_active: initialData.is_active || false,
        restriction_type: initialData.restriction_type || "HOURS",
        hours: initialData.hours || "1",
        days: initialData.days || {},
        time_ranges: formattedTimeRanges,
        school: initialData.school || schoolId,
      });
    }
  }, [isEditMode, initialData, isOpen, reset, schoolId]);

  // Reset fields when toggle is disabled
  useEffect(() => {
    if (!isEnabled) {
      setValue("restriction_type", "HOURS");
      setValue("hours", "1");
      setValue("days", {});
      setValue("time_ranges", {});
    }
  }, [isEnabled, setValue]);

  const handleCancel = () => {
    reset();
    onClose();
  };

  const formatTimeString = (timeStr: string | undefined): string => {
    if (!timeStr) return "";
    return moment(timeStr, "HH:mm").format("hh:mmA");
  };

  const isTimeBeforeOrEqual = (time1: string, time2: string): boolean => {
    if (!time1 || !time2) return false;

    // Parse the times to get hours and minutes
    const parseTime = (timeStr: string) => {
      // Handle 24-hour format (HH:MM) from native time input
      const match24Hour = timeStr.match(/^(\d{1,2}):(\d{2})$/);
      if (match24Hour) {
        const hours = parseInt(match24Hour[1], 10);
        const minutes = parseInt(match24Hour[2], 10);
        return { hours, minutes };
      }

      // Also handle 12-hour format (HH:MM AM/PM) for backward compatibility
      const match12Hour = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/i);
      if (match12Hour) {
        let hours = parseInt(match12Hour[1], 10);
        const minutes = parseInt(match12Hour[2], 10);
        const period = match12Hour[3].toUpperCase();

        // Convert to 24-hour format for comparison
        if (period === "PM" && hours < 12) hours += 12;
        if (period === "AM" && hours === 12) hours = 0;

        return { hours, minutes };
      }

      return null;
    };

    const t1 = parseTime(time1);
    const t2 = parseTime(time2);

    if (!t1 || !t2) return false;

    // Compare hours first, then minutes
    if (t1.hours < t2.hours) return true;
    if (t1.hours > t2.hours) return false;
    return t1.minutes <= t2.minutes; // Using <= to check if times are equal or before
  };

  const areTimesEqual = (time1: string, time2: string): boolean => {
    if (!time1 || !time2) return false;

    // Parse the times to get hours and minutes
    const parseTime = (timeStr: string) => {
      // Handle 24-hour format (HH:MM) from native time input
      const match24Hour = timeStr.match(/^(\d{1,2}):(\d{2})$/);
      if (match24Hour) {
        const hours = parseInt(match24Hour[1], 10);
        const minutes = parseInt(match24Hour[2], 10);
        return { hours, minutes };
      }

      // Also handle 12-hour format (HH:MM AM/PM) for backward compatibility
      const match12Hour = timeStr.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)$/i);
      if (match12Hour) {
        let hours = parseInt(match12Hour[1], 10);
        const minutes = parseInt(match12Hour[2], 10);
        const period = match12Hour[3].toUpperCase();

        // Convert to 24-hour format for comparison
        if (period === "PM" && hours < 12) hours += 12;
        if (period === "AM" && hours === 12) hours = 0;

        return { hours, minutes };
      }

      return null;
    };

    const t1 = parseTime(time1);
    const t2 = parseTime(time2);

    if (!t1 || !t2) return false;

    // Check if hours and minutes are the same
    return t1.hours === t2.hours && t1.minutes === t2.minutes;
  };

  const handleFormSubmit = (data: RestrictionFormData) => {
    let processedDays: { [key: string]: boolean } | null = {
      MONDAY: false,
      TUESDAY: false,
      WEDNESDAY: false,
      THURSDAY: false,
      FRIDAY: false,
      SATURDAY: false,
      SUNDAY: false,
    };

    if (data.restriction_type === "HOURS" || data.restriction_type === "DAYS") {
      if (data.days && Object.keys(data.days).length > 0) {
        // Initialize all days as false
        processedDays = {
          MONDAY: false,
          TUESDAY: false,
          WEDNESDAY: false,
          THURSDAY: false,
          FRIDAY: false,
          SATURDAY: false,
          SUNDAY: false,
        };

        // Set selected days to true
        Object.keys(data.days).forEach((day) => {
          if (data.days && data.days[day as keyof typeof data.days]) {
            if (processedDays) {
              processedDays[day] = true;
            }
          }
        });
      }
    }

    // Format time values by removing spaces between time and AM/PM when restriction_type is 'TIME'
    let formattedTimeRanges = {
      MONDAY: { start: "", end: "" },
      TUESDAY: { start: "", end: "" },
      WEDNESDAY: { start: "", end: "" },
      THURSDAY: { start: "", end: "" },
      FRIDAY: { start: "", end: "" },
      SATURDAY: { start: "", end: "" },
      SUNDAY: { start: "", end: "" },
    };

    if (data.restriction_type === "TIME" && data.time_ranges) {
      // Process each day's time ranges
      Object.keys(data.time_ranges).forEach((day) => {
        const dayKey = day as keyof typeof data.time_ranges;
        const timeRange = data.time_ranges?.[dayKey] as {
          start?: string;
          end?: string;
        };

        if (timeRange) {
          formattedTimeRanges[dayKey as keyof typeof formattedTimeRanges] = {
            start: formatTimeString(timeRange.start),
            end: formatTimeString(timeRange.end),
          };
        }
      });
    }

    const formattedData: SchoolRestrictionModel = {
      school: data.school,
      restriction_type: data.restriction_type,
      is_active: data.is_active,
      hours: data.restriction_type === "HOURS" ? data.hours : "",
      days: processedDays,
      time_ranges:
        data.restriction_type === "TIME"
          ? formattedTimeRanges
          : formattedTimeRanges,
    };

    dispatch(updateRestriction(formattedData))
      .unwrap()
      .then((res) => {
        console.log("formattedData", res);
        onRefresh?.();
        onClose();
      });
  };

  const weekendDays = [
    { id: "saturday", label: "Saturday", apiKey: "SATURDAY" },
    { id: "sunday", label: "Sunday", apiKey: "SUNDAY" },
  ];

  // Day options for full week selection
  const weekDays = [
    { id: "monday", label: "Monday", apiKey: "MONDAY" },
    { id: "tuesday", label: "Tuesday", apiKey: "TUESDAY" },
    { id: "wednesday", label: "Wednesday", apiKey: "WEDNESDAY" },
    { id: "thursday", label: "Thursday", apiKey: "THURSDAY" },
    { id: "friday", label: "Friday", apiKey: "FRIDAY" },
    { id: "saturday", label: "Saturday", apiKey: "SATURDAY" },
    { id: "sunday", label: "Sunday", apiKey: "SUNDAY" },
  ];

  // Day options for time range selection
  const timeRangeDays = [
    { id: "mon", label: "Mon", apiKey: "MONDAY" },
    { id: "tue", label: "Tue", apiKey: "TUESDAY" },
    { id: "wed", label: "Wed", apiKey: "WEDNESDAY" },
    { id: "thu", label: "Thu", apiKey: "THURSDAY" },
    { id: "fri", label: "Fri", apiKey: "FRIDAY" },
    { id: "sat", label: "Sat", apiKey: "SATURDAY" },
    { id: "sun", label: "Sun", apiKey: "SUNDAY" },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="School Session Restriction"
      width="775px"
      contentClassName="p-0 overflow-hidden"
    >
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="px-6 pt-4 mb-[34px] overflow-y-auto max-h-[calc(100vh-270px)]">
          <div className="mb-6">
            <p className="text-[16px] text-[#2D3748] font-medium mb-[17px]">
              Enable Restriction
            </p>
            <div className="w-[46px]">
              <Controller
                name="is_active"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <>
                    {value ? (
                      <div
                        className="cursor-pointer"
                        onClick={() => onChange(false)}
                      >
                        <ToggleSwitchOnSVG />
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer"
                        onClick={() => onChange(true)}
                      >
                        <ToggleSwitchOffSVG />
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className="text-[#2D3748]">
            {isEnabled && (
              <>
                <Controller
                  name="restriction_type"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <SelectWithRadio
                      id="restriction_type"
                      label="Restriction"
                      placeholder="Select restriction type"
                      options={RESTRICTION.map((item) => ({
                        ...item,
                        value: item.value.toUpperCase(),
                      }))}
                      value={value}
                      onChange={(val) => {
                        if (isEditMode && initialData && !isCleared) {
                          setClearConfirmModal({
                            isOpen: true,
                            type: val as "HOURS" | "DAYS" | "TIME",
                          });
                        } else {
                          onChange(val as "HOURS" | "DAYS" | "TIME");
                        }
                      }}
                    />
                  )}
                />

                {restrictionType === "HOURS" && (
                  <>
                    <div className="mt-4 space-x-6 flex items-center">
                      {/* Hours Input */}
                      <div className="relative flex items-center">
                        <Controller
                          name="hours"
                          control={control}
                          render={({ field }) => (
                            <>
                              <Input
                                type="tel"
                                label="Hours"
                                value={field.value}
                                onChange={(e) => {
                                  // Only allow numeric input
                                  const value = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  field.onChange(value);
                                }}
                                className={`w-[94px] pr-10 rounded-xl ${
                                  (parseInt(field.value || "0") <= 0 ||
                                    (restrictionType === "HOURS" &&
                                      parseInt(field.value || "0") >
                                        getMaxHours())) &&
                                  restrictionType === "HOURS"
                                    ? "border-red-500"
                                    : ""
                                }`}
                              />
                            </>
                          )}
                        />
                        <span className="absolute right-5 bottom-3 text-gray-500">
                          Hrs
                        </span>
                      </div>

                      {/* checkboxes */}
                      <div className="relative flex items-center h-[82px]">
                        <div className="w-full flex flex-col space-y-2">
                          <label className="text-[#2D3748] text-[16px] font-medium">
                            Include
                          </label>
                          <div className="flex items-center gap-4 h-[50px]">
                            {weekendDays.map((day) => (
                              <div
                                key={day.id}
                                className="flex items-center gap-4"
                              >
                                <Controller
                                  name={`days.${day.apiKey}`}
                                  control={control}
                                  render={({ field: { value, onChange } }) => (
                                    <Checkbox
                                      checked={value || false}
                                      onCheckedChange={onChange}
                                      label={day.label}
                                    />
                                  )}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    {restrictionType === "HOURS" && getHoursErrorMessage() && (
                      <p className="text-red-500 text-xs mt-1">
                        {getHoursErrorMessage()}
                      </p>
                    )}
                  </>
                )}

                {restrictionType === "DAYS" && (
                  <div className="mt-4 flex flex-col">
                    <label className="text-[#2D3748] text-[16px] font-medium">
                      Days
                    </label>
                    <div className="flex flex-wrap items-center mt-[19px] mb-2">
                      {weekDays.map((day) => (
                        <div key={day.id} className="flex items-center gap-2">
                          <Controller
                            name={`days.${day.apiKey}`}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <Checkbox
                                label={day.label}
                                checked={value || false}
                                onCheckedChange={onChange}
                              />
                            )}
                          />
                        </div>
                      ))}
                    </div>
                    {getDaysErrorMessage() && (
                      <p className="text-red-500 text-xs">
                        {getDaysErrorMessage()}
                      </p>
                    )}
                  </div>
                )}

                {restrictionType === "TIME" && (
                  <div className="mt-4 flex flex-col">
                    <label className="text-[#2D3748] text-[16px] font-medium">
                      Select Range
                    </label>
                    <div className="flex flex-wrap gap-[24px] mt-[16px]">
                      {timeRangeDays.map((day) => (
                        <div
                          key={day.id}
                          className="flex items-center gap-3 w-[320px]"
                        >
                          <label
                            htmlFor="select-all"
                            className={`w-[30px] text-[#354052] text-[14px]`}
                          >
                            {day.label}
                          </label>

                          {/* Start Time */}
                          <Controller
                            name={`time_ranges.${day.apiKey}.start`}
                            control={control}
                            defaultValue=""
                            render={({ field: { value, onChange } }) => (
                              <TimePickerDefault
                                label=""
                                placeholder="time"
                                containerClassName="w-[122px] h-[35px]"
                                value={value || ""}
                                onChange={onChange}
                              />
                            )}
                          />

                          <span>–</span>

                          {/* End Time */}
                          <Controller
                            name={`time_ranges.${day.apiKey}.end`}
                            control={control}
                            defaultValue=""
                            render={({ field: { value, onChange } }) => (
                              <TimePickerDefault
                                label=""
                                placeholder="time"
                                containerClassName="w-[122px] h-[35px]"
                                value={value || ""}
                                onChange={onChange}
                              />
                            )}
                          />
                        </div>
                      ))}
                    </div>
                    {getTimeRangesErrorMessage() && (
                      <p className="text-red-500 text-xs mt-1">
                        {getTimeRangesErrorMessage()}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            type="button"
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            disabled={actionLoader || !isFormValid()}
            loading={actionLoader}
          >
            {isEditMode ? "Update" : "Restrict"}
          </Button>
        </div>
      </form>

      {/* Delete School Confirmation Modal */}
      <Modal
        isOpen={clearConfirmModal.isOpen}
        onClose={() => setClearConfirmModal({ isOpen: false, type: "HOURS" })}
        title="Clear Restriction"
        width="728px"
        contentClassName="p-0"
      >
        <div className="p-6">
          <p className="text-[#7B7B7B] text-[24px] mb-8">
            Changing the restriction type will clear all the data. Are you sure
            want to clear restriction ?
          </p>
        </div>
        <div className="flex justify-end border-t border-gray-200 p-4 gap-4">
          <Button
            variant="light"
            size="md"
            className="w-[120px] h-[48px] bg-[#fff] text-dark border-[1px] border-[#5C88C5]"
            onClick={() =>
              setClearConfirmModal({ isOpen: false, type: "HOURS" })
            }
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            className="w-[142px] h-[48px] bg-[#5C88C5] text-white"
            onClick={() => {
              reset({
                is_active: true,
                restriction_type: clearConfirmModal.type as
                  | "HOURS"
                  | "DAYS"
                  | "TIME",
                hours: "1",
                days: {},
                time_ranges: {},
                school: schoolId,
              });
              setClearConfirmModal({ isOpen: false, type: "HOURS" });
              setIsCleared(true);
            }}
          >
            Yes, Clear
          </Button>
        </div>
      </Modal>
    </Modal>
  );
}

export default RestrictionModal;
